/**
 * Route API Hook
 *
 * Encapsulates all API calls for BFT route operations.
 * Provides typed functions for searching, loading, saving, and deleting routes.
 */
import { useCallback } from 'react';
import { parseNois, } from '../types/route-editor';
/**
 * Hook providing API functions for route operations
 */
export function useRouteApi() {
    /**
     * Search routes by text (matches our_ref or description)
     */
    const searchRoutes = useCallback(async (searchText) => {
        const response = await fetch(`/bft/?search=${encodeURIComponent(searchText)}`);
        if (!response.ok) {
            throw new Error(`Search failed: ${response.statusText}`);
        }
        return response.json();
    }, []);
    /**
     * Load a route by BFT reference
     */
    const loadRoute = useCallback(async (bftRef) => {
        const response = await fetch(`/bft/${encodeURIComponent(bftRef)}`);
        if (!response.ok) {
            throw new Error(`Failed to load route: ${response.statusText}`);
        }
        const data = await response.json();
        // Parse NOIs for each duct feature
        if (data.featureCollection?.features) {
            data.featureCollection.features.forEach(feature => {
                if (feature.geometry.type === 'LineString') {
                    const ductFeature = feature;
                    ductFeature.nois = parseNois(ductFeature.properties.ORREFERENCEIDS);
                }
            });
        }
        return data;
    }, []);
    /**
     * Save a new or existing route
     */
    const saveRoute = useCallback(async (metadata, ducts) => {
        // Build the request payload
        const ductData = Array.from(ducts.values())
            .filter(d => d.geometry.type === 'LineString')
            .map(d => ({
            objectID: d.properties.OBJECTID,
            conduitID: d.properties.DB_ID,
            NOI: d.properties.BFT_PIANOI || '',
            status: d.properties.BFT_STATUS,
        }));
        const payload = {
            'bft-ref': metadata.ourRef,
            'bft-type': metadata.cableType,
            'bft-description': metadata.description,
            'rfs-toggle': metadata.rfs ? 'on' : 'off',
            ducts: ductData,
        };
        // Include ID for updates
        if (metadata.id) {
            payload['bft-id'] = metadata.id;
        }
        try {
            const response = await fetch(`/bft/${encodeURIComponent(metadata.ourRef)}`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(payload),
            });
            const data = await response.json();
            if (!response.ok) {
                // Handle validation errors
                if (data.errors) {
                    const errors = data.errors;
                    const errorMessages = Object.entries(errors)
                        .map(([field, msg]) => `${field}: ${msg}`)
                        .join(', ');
                    return { success: false, error: errorMessages };
                }
                return { success: false, error: response.statusText };
            }
            return {
                success: true,
                id: data.id,
            };
        }
        catch (err) {
            return {
                success: false,
                error: err instanceof Error ? err.message : 'Unknown error',
            };
        }
    }, []);
    /**
     * Delete a route by ID
     */
    const deleteRoute = useCallback(async (id) => {
        try {
            const response = await fetch(`/bft/${id}`, {
                method: 'DELETE',
            });
            if (!response.ok) {
                const data = await response.json();
                return {
                    success: false,
                    error: data.error || response.statusText,
                };
            }
            return { success: true };
        }
        catch (err) {
            return {
                success: false,
                error: err instanceof Error ? err.message : 'Unknown error',
            };
        }
    }, []);
    /**
     * Get next available BFT ID for a subtype (BMD, DMD, BLD, DSD)
     */
    const getNextId = useCallback(async (subtype) => {
        const response = await fetch(`/bft/nextID/${encodeURIComponent(subtype)}`);
        if (!response.ok) {
            throw new Error(`Failed to get next ID: ${response.statusText}`);
        }
        return response.text();
    }, []);
    /**
     * Fetch nearest duct to a location using CFN.getNearestDuct
     * Returns null if no duct found or CFN function not available
     */
    const fetchNearestDuct = useCallback(async (lat, lng) => {
        return new Promise((resolve) => {
            const CFN = window.CFN;
            if (!CFN?.getNearestDuct) {
                console.warn('CFN.getNearestDuct not available');
                resolve(null);
                return;
            }
            CFN.getNearestDuct({ lat, lng }, (data) => {
                if (data?.features?.length > 0) {
                    const feature = data.features[0];
                    const props = feature.properties;
                    // Uppercase property names (CFN.featureFilter does this)
                    const uppercasedProps = {};
                    Object.entries(props).forEach(([key, value]) => {
                        uppercasedProps[key.toUpperCase()] = value;
                    });
                    // Ensure DB_ID is a string
                    uppercasedProps.DB_ID = String(props.db_id || props.DB_ID);
                    // Create the duct feature
                    const ductFeature = {
                        type: 'Feature',
                        geometry: feature.geometry,
                        properties: uppercasedProps,
                        nois: parseNois(uppercasedProps.ORREFERENCEIDS),
                    };
                    resolve(ductFeature);
                }
                else {
                    resolve(null);
                }
            });
        });
    }, []);
    return {
        searchRoutes,
        loadRoute,
        saveRoute,
        deleteRoute,
        getNextId,
        fetchNearestDuct,
    };
}
export default useRouteApi;
